import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

const RandomNumberGenerator = () => {
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleMinChange = (e) => {
    setMin(e.target.value);
    setError('');
  };

  const handleMaxChange = (e) => {
    setMax(e.target.value);
    setError('');
  };

  const generateRandomNumber = () => {
    if (!min || !max) {
      setError('Please enter both minimum and maximum values.');
      return;
    }

    const minNum = parseFloat(min);
    const maxNum = parseFloat(max);

    if (isNaN(minNum) || isNaN(maxNum)) {
      setError('Please enter valid numbers for minimum and maximum.');
      return;
    }

    if (minNum >= maxNum) {
      setError('Minimum value must be less than maximum value.');
      return;
    }

    const randomNumber = Math.random() * (maxNum - minNum) + minNum;
    setResult(randomNumber.toFixed(2));
  };

  return (
    <>
      <Helmet>
        <title>Random Number Generator | Biology Tools</title>
        <meta name="description" content="Generate random numbers within a specified range. Useful for various scientific and statistical applications." />
        <meta property="og:title" content="Random Number Generator" />
        <meta property="og:description" content="Generate random numbers within a specified range with ease." />
        <meta property="og:image" content="/images/random-number-generator-preview.jpg" />
        <meta property="og:url" content="https://yourdomain.com/biology/random-number-generator" />
        <title>Random Number Generator | Biology Tools</title>
        <meta name="description" content="Generate random numbers within a specified range. Useful for various scientific and statistical applications." />
        <meta name="keywords" content="random number generator, random numbers, range, scientific calculator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Random Number Generator" />
        <meta property="og:description" content="Generate random numbers within a specified range with ease." />
        <meta property="og:image" content="/images/random-number-generator-preview.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Random Number Generator" />
        <meta name="twitter:description" content="Generate random numbers within a specified range with ease." />
        <meta name="twitter:image" content="/images/random-number-generator-preview.jpg" />
        
        {/* Additional metadata */}
        <meta name="application-name" content="Biology Calculator Tools" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Random Number Generator" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#4CAF50" />
      </Helmet>

      <div className="w-full min-h-screen p-4 md:p-8 bg-gray-50">
        {/* Header section with image */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <img 
              src="/images/biology/dna-icon.svg" 
              alt="DNA Helix Icon" 
              className="w-12 h-12 md:w-16 md:h-16"
              loading="lazy"
            />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Random Number Generator
              </h1>
              <p className="text-base md:text-lg text-blue-600 font-semibold">
                Scientific and Statistical Tool
              </p>
            </div>
          </div>
        </div>

        {/* Main content section with image */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-4">
            <img
              src="/images/biology/random-number-visual.svg"
              alt="Random Number Visualization"
              className="w-24 h-24 object-contain"
              loading="lazy"
            />
            <p className="text-sm md:text-base text-gray-700">
              Generate random numbers within a specified range. Useful for various scientific and statistical applications.
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4 md:p-8 bg-white rounded-xl shadow-lg">
          <div className="mb-6">
            <label htmlFor="min" className="block text-gray-700 font-medium mb-2">
              Minimum Value:
            </label>
            <input
              type="number"
              id="min"
              name="min"
              value={min}
              onChange={handleMinChange}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base md:text-lg"
              placeholder="Enter minimum value"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="max" className="block text-gray-700 font-medium mb-2">
              Maximum Value:
            </label>
            <input
              type="number"
              id="max"
              name="max"
              value={max}
              onChange={handleMaxChange}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base md:text-lg"
              placeholder="Enter maximum value"
            />
          </div>

          <button
            onClick={generateRandomNumber}
            className="w-full py-3 px-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-colors duration-200 text-base md:text-lg"
          >
            Generate
          </button>

          {error && (
            <div className="mt-4 text-center text-red-500">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Result</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-lg font-semibold mb-2">Random Number</h4>
                <p className="text-gray-700">{result}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RandomNumberGenerator;